import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SidePanel } from '../components/SidePanel';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Loader2, Pencil, Plus, X } from 'lucide-react';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { vehicleApi } from '../services/vehicleApi';
import { cashflowApi } from '../services/cashflowApi';
import {
  DEAL_STAGES, Deal, DealStage, SOURCE_LABEL, STAGE_STYLE, VALUATION_SOURCES, Valuation, ValuationForm, ValuationSource, ACQUISITION_LABEL, Acquisition, SECURITY_KINDS, SecurityKind } from '../types/deal';
import { Company } from '../types/company';
import { KIND_STYLE, Participant, Vehicle, won, KIND_LABEL } from '../types/vehicle';
import { Cashflow, KIND_META } from '../types/cashflow';
import { mult } from '../lib/metrics';
import { CashflowFormModal } from '../components/CashflowFormModal';
import { DealTermsSection } from '../components/DealTermsSection';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { todayLocal } from '../lib/date';

/** 투자 건 상세 — 이 건의 돈과 평가 이력 */
/** 주소로 들어올 때(/deals/:id) — 다른 화면의 링크용. 투자처 · 자금원 상세에서는 오른쪽 패널로 연다 */
export const DealDetailPage: React.FC = () => {
  const { id } = useParams();
  return <DealDetail dealId={Number(id)} />;
};

/** 투자 건 상세 본문 — 화면 전체 · 오른쪽 패널 둘 다 이걸 쓴다 */
export const DealDetail: React.FC<{ dealId: number; inPanel?: boolean }> = ({ dealId, inPanel = false }) => {
  const navigate = useNavigate();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [vals, setVals] = useState<Valuation[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [parts, setParts] = useState<Participant[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [tourRun, setTourRun] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await dealApi.get(dealId);
      setDeal(d ?? null);
      if (d) {
        const [c, v, f, vs, allV, allP, allD, allC] = await Promise.all([
          companyApi.get(d.companyId), vehicleApi.get(d.vehicleId),
          cashflowApi.list(), dealApi.valuations(),
          vehicleApi.list(), vehicleApi.allParticipants(), dealApi.list(), companyApi.list(),
        ]);
        setVehicles(allV); setParts(allP); setDeals(allD); setCompanies(allC);
        setCompany(c ?? null);
        setVehicle(v ?? null);
        setFlows(f.filter((x) => x.dealId === dealId));
        setVals(vs.filter((x) => x.dealId === dealId).sort((a, b) => b.valuedOn.localeCompare(a.valuedOn)));
      }
    } finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const TOUR: TourStep[] = useMemo(() => [
    {
      name: 'head',
      target: '[data-tour="head"]',
      title: '이 건이 무엇인지',
      content: '어느 투자처에, 어느 자금원에서, 어떤 증권으로 넣었는지입니다. 회사나 자금원 이름을 누르면 그 상세로 갑니다.',
    },
    {
      name: 'stage',
      target: '[data-tour="stage"]',
      title: '단계',
      content: '검토 → 투자 → 회수 또는 상각. 전부 팔았으면 「회수」, 가치가 없어졌으면 「상각」으로 바꿉니다. 끝난 건은 평가액을 세지 않습니다.',
      placement: 'left',
    },
    {
      name: 'addflow',
      target: '[data-tour="addflow"]',
      title: '기록 추가',
      content: '이 건의 자금원과 건이 골라진 채로 원장 입력이 열립니다. 투자금이 나간 날, 회수금이 들어온 날 적습니다.',
      placement: 'left',
    },
    {
      name: 'cards',
      target: '[data-tour="cards"]',
      title: '이 건의 성적',
      content: '투자와 회수는 원장에서, 평가는 최신 평가액에서 옵니다. 회수되거나 상각된 건은 평가액을 세지 않습니다.',
    },
    {
      name: 'flows',
      target: '[data-tour="flows"]',
      title: '이 건의 원장',
      content: '원장에서 이 건에 걸린 줄만 모았습니다. 지우려면 원장 화면에서 합니다.',
    },
    {
      name: 'vals',
      target: '[data-tour="vals"]',
      title: '평가액',
      content: '라운드가 있었거나 값을 다시 매길 때 추가합니다. 덮어쓰지 않고 쌓아서 지난 평가를 되돌아볼 수 있습니다.',
      placement: 'left',
    },
    {
      name: 'terms',
      target: '[data-tour="terms"]',
      title: '계약조건',
      content: '투자계약서에서 옮겨 적습니다. 상환 · 전환할 수 있는 날, 오늘 상환하면 얼마인지, 계약이 끝났는지(지분 문턱 · 상장)를 계산해 보입니다. 사전협의 없이 진행한 일을 기록하면 계약의 횟수에 닿을 때 빨간 경고가 뜹니다.',
    },
  ], []);

  const setStage = async (stage: DealStage) => {
    await dealApi.update(dealId, { stage });
    load();
  };

  if (loading && !deal) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> 불러오는 중...
      </div>
    );
  }
  if (!deal) {
    return <div className="p-6 lg:p-8"><p className="text-sm text-slate-500">투자 건을 찾을 수 없습니다.</p></div>;
  }

  const invested = flows.filter((f) => f.kind === 'investment').reduce((s, f) => s + f.amount, 0);
  const returned = flows.filter((f) => f.kind === 'exit' || f.kind === 'dividend').reduce((s, f) => s + f.amount, 0);
  const closed = deal.stage === '회수' || deal.stage === '상각';
  const residual = closed ? 0 : (vals[0]?.ourValue ?? 0);
  // 평가액이 없으면 배수를 「—」로 — 0 으로 세면 0.00x(전액 손실)처럼 보인다
  const moic = invested > 0 && (closed || vals.length > 0) ? (returned + residual) / invested : null;

  return (
    <div className={inPanel ? '' : 'p-6 lg:p-8 max-w-5xl'}>
      {!inPanel && (
        <button onClick={() => navigate(`/companies/${deal.companyId}`)}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2">
          <ChevronLeft size={14} /> {company?.name ?? '투자처'}
        </button>
      )}

      <div data-tour="head" className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            {company ? <Link to={`/companies/${company.id}`} className="hover:text-indigo-600">{company.name}</Link> : '—'}
            <TourButton onClick={() => setTourRun(true)} />
          </h1>
          <p className="mt-1 text-sm text-slate-500 flex items-center gap-1.5">
            {vehicle && <Link to={`/vehicles/${vehicle.id}`} className="hover:text-indigo-600">{vehicle.name}</Link>}
            {vehicle && (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${KIND_STYLE[vehicle.kind]}`}>
                {KIND_LABEL[vehicle.kind]}
              </span>
            )}
            {deal.securityKind && <span className="text-slate-400">· {deal.securityKind}</span>}
            <select aria-label="인수 방식" value={deal.acquisition ?? 'new'}
              onChange={async (e) => { const a = e.target.value as Acquisition; await dealApi.update(deal.id, { acquisition: a }); setDeal({ ...deal, acquisition: a }); }}
              className="rounded border-0 bg-transparent py-0 pl-1 pr-6 text-xs text-slate-500 focus:ring-1 focus:ring-indigo-300">
              {(['new', 'secondary'] as Acquisition[]).map((a) => <option key={a} value={a}>{ACQUISITION_LABEL[a]}</option>)}
            </select>
          </p>
          {(deal.shares || deal.pricePerShare || deal.investedOn) && (
            <p className="mt-1 text-xs text-slate-600 tabular-nums">
              {deal.investedOn && <>{deal.investedOn.replace(/-/g, '.')} 투자</>}
              {deal.shares ? <> · {deal.shares.toLocaleString()}주</> : null}
              {deal.pricePerShare ? <> × {deal.pricePerShare.toLocaleString()}원</> : null}
              {deal.shares && deal.pricePerShare ? <span className="text-slate-400"> = {(deal.shares * deal.pricePerShare).toLocaleString()}원</span> : null}
            </p>
          )}
          {deal.note && <p className="mt-1 text-xs text-slate-400 max-w-xl">{deal.note}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select data-tour="stage" aria-label="단계" value={deal.stage} onChange={(e) => setStage(e.target.value as DealStage)}
            className={`rounded-full px-2.5 py-1 text-xs font-medium border-0 focus:ring-2 focus:ring-indigo-300 ${STAGE_STYLE[deal.stage]}`}>
            {DEAL_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => setEditOpen(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
            <Pencil size={12} /> 수정
          </button>
          <button data-tour="addflow" onClick={() => setFlowOpen(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus size={13} /> 기록 추가
          </button>
        </div>
      </div>

      <div data-tour="cards" className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        {[
          ['투자', won(invested), ''],
          ['회수', returned > 0 ? won(returned) : '—', ''],
          ['평가', residual > 0 ? won(residual) : '—', ''],
          ['배수', mult(moic), ''],
        ].map(([k, v, note]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
            <div className="text-xs text-slate-400">{k}</div>
            <div className="mt-0.5 text-base font-bold text-slate-900 tabular-nums">{v}</div>
            {note && <div className="text-xs text-slate-400">{note}</div>}
          </div>
        ))}
      </div>

      <div className={`grid gap-4 ${inPanel ? '' : 'lg:grid-cols-2'}`}>
        <section data-tour="flows" className="bg-white border border-slate-200 rounded-lg">
          <h2 className="text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">이 건의 원장</h2>
          {flows.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">아직 기록이 없습니다.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {[...flows].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn)).map((f) => (
                  <tr key={f.id} className="border-b border-slate-50 last:border-b-0">
                    <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap tabular-nums">
                      {f.occurredOn.replace(/-/g, '.')}
                    </td>
                    <td className="px-2 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${KIND_META[f.kind].style}`}>
                        {KIND_META[f.kind].label}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 text-right font-medium whitespace-nowrap tabular-nums ${
                      f.direction === 'in' ? 'text-emerald-700' : 'text-slate-900'
                    }`}>
                      {f.direction === 'in' ? '+' : '−'}{won(f.amount).slice(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section data-tour="vals" className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">평가액</h2>
            {closed ? (
              <span className="text-xs text-slate-400">끝난 건 — 평가를 더 넣지 않습니다</span>
            ) : (
              <button onClick={() => setOpen(true)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50">
                <Plus size={11} /> 추가
              </button>
            )}
          </div>
          {vals.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">{closed ? '끝난 건이라 평가액을 세지 않습니다.' : '아직 매긴 값이 없습니다.'}</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {vals.map((v, i) => (
                  <tr key={v.id} className="border-b border-slate-50 last:border-b-0">
                    <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap tabular-nums">
                      {v.valuedOn.replace(/-/g, '.')}
                      {i === 0 && <span className="ml-1 text-indigo-600 font-medium">최신</span>}
                    </td>
                    <td className="px-2 py-2.5 text-xs text-slate-500">{SOURCE_LABEL[v.source]}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-900 whitespace-nowrap tabular-nums">
                      {won(v.ourValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <DealTermsSection deal={deal} listed={!!company?.listed} invested={invested}
        dividends={flows.filter((f) => f.kind === 'dividend').reduce((s, f) => s + f.amount, 0)} />

      <DealEditModal open={editOpen} deal={deal} onClose={() => setEditOpen(false)}
        onSaved={(d) => { setEditOpen(false); setDeal(d); }} />
      <CashflowFormModal open={flowOpen} vehicles={vehicles} parts={parts} deals={deals} companies={companies}
        initial={{ vehicleId: deal.vehicleId, dealId: deal.id, kind: 'investment' }}
        onClose={() => setFlowOpen(false)} onSaved={() => { setFlowOpen(false); load(); }} />

      <ValuationModal open={open} dealId={dealId} onClose={() => setOpen(false)}
        onSaved={() => { setOpen(false); load(); }} />

      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};

const EMPTY = (): ValuationForm => ({
  valuedOn: todayLocal(),
  ourValue: null, companyValue: null, source: 'manual', note: '',
});

const ValuationModal: React.FC<{
  open: boolean; dealId: number; onClose: () => void; onSaved: () => void;
}> = ({ open, dealId, onClose, onSaved }) => {
  const [form, setForm] = useState<ValuationForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  // 실패하면 창 안에 이유를 보인다 — 던지면 단추가 아무 반응 없이 멈춘다(실사용 테스트 7회차)
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) setForm(EMPTY()); }, [open]);
  if (!open) return null;

  const set = <K extends keyof ValuationForm>(k: K, v: ValuationForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const input = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';
  const label = 'block text-xs font-medium text-slate-500 mb-1';

  const save = async () => {
    setSaving(true); setErr(null);
    try { await dealApi.addValuation(dealId, form); onSaved(); } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">평가액 추가</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="v-date">평가 시점</label>
              <input id="v-date" type="date" max={todayLocal()} className={input} value={form.valuedOn}
                onChange={(e) => set('valuedOn', e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="v-src">근거</label>
              <select id="v-src" className={input} value={form.source}
                onChange={(e) => set('source', e.target.value as ValuationSource)}>
                {VALUATION_SOURCES.map((s) => (
                  <option key={s} value={s}>{SOURCE_LABEL[s]}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={label} htmlFor="v-our">우리 보유분 평가액 (원)</label>
            <input id="v-our" type="number" min={0} step={10000000} className={input + ' text-right'}
              value={form.ourValue ?? ''}
              onChange={(e) => set('ourValue', e.target.value === '' ? null : Number(e.target.value))} />
          </div>
          <div>
            <label className={label} htmlFor="v-comp">회사 전체 가치 (원) <span className="text-slate-400">— 참고</span></label>
            <input id="v-comp" type="number" min={0} step={100000000} className={input + ' text-right'}
              value={form.companyValue ?? ''}
              onChange={(e) => set('companyValue', e.target.value === '' ? null : Number(e.target.value))} />
          </div>
          <div>
            <label className={label} htmlFor="v-note">메모</label>
            <input id="v-note" className={input} value={form.note} onChange={(e) => set('note', e.target.value)} />
          </div>
        </div>
        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={save} disabled={(form.ourValue ?? 0) <= 0 || saving}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {saving ? '저장 중...' : '추가'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};

/**
 * 투자 건 수정 — 투자일 · 증권 · 인수 방식 · 취득 주식 수 · 주당 취득가 · 비고 (2026-11-02).
 * 추가 창에 없던 주식 수를 나중에 넣을 곳이 없었다. 돈(투자 · 회수)은 원장에 적는다 — 여기는 계약상 값이다.
 */
const DealEditModal: React.FC<{ open: boolean; deal: Deal; onClose: () => void; onSaved: (d: Deal) => void }> = ({ open, deal, onClose, onSaved }) => {
  const [f, setF] = useState({ investedOn: '', securityKind: '' as SecurityKind | '', acquisition: 'new' as Acquisition, shares: '', pricePerShare: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setF({
      investedOn: deal.investedOn ?? '', securityKind: deal.securityKind ?? '', acquisition: deal.acquisition ?? 'new',
      shares: deal.shares == null ? '' : String(deal.shares), pricePerShare: deal.pricePerShare == null ? '' : String(deal.pricePerShare), note: deal.note ?? '',
    });
    setErr(null);
  }, [open, deal]);
  if (!open) return null;
  const input = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';
  const label = 'block text-xs font-medium text-slate-500 mb-1';
  const n = (x: string) => (x.trim() === '' ? null : Number(x));
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const d = await dealApi.update(deal.id, {
        investedOn: f.investedOn || null, securityKind: (f.securityKind || null) as Deal['securityKind'], acquisition: f.acquisition,
        shares: n(f.shares), pricePerShare: n(f.pricePerShare), note: f.note.trim(),
      });
      onSaved(d);
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };
  const total = n(f.shares) && n(f.pricePerShare) ? n(f.shares)! * n(f.pricePerShare)! : null;
  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">투자 건 수정</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="de-date">투자일</label>
              <input id="de-date" type="date" className={input} value={f.investedOn} onChange={(e) => setF({ ...f, investedOn: e.target.value })} />
            </div>
            <div>
              <label className={label} htmlFor="de-sec">증권</label>
              <select id="de-sec" className={input} value={f.securityKind} onChange={(e) => setF({ ...f, securityKind: e.target.value as SecurityKind | '' })}>
                <option value="">미정</option>
                {SECURITY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="de-shares">취득 주식 수</label>
              <input id="de-shares" type="number" min={0} className={input + ' text-right'} value={f.shares} onChange={(e) => setF({ ...f, shares: e.target.value })} />
            </div>
            <div>
              <label className={label} htmlFor="de-price">주당 취득가 (원)</label>
              <input id="de-price" type="number" min={0} className={input + ' text-right'} value={f.pricePerShare} onChange={(e) => setF({ ...f, pricePerShare: e.target.value })} />
            </div>
          </div>
          {total !== null && <p className="text-xs text-slate-500">취득가액 {total.toLocaleString()}원 — 실제 돈은 원장 「투자」로 적습니다</p>}
          <div>
            <label className={label}>인수 방식</label>
            <div className="flex gap-2">
              {(['new', 'secondary'] as Acquisition[]).map((a) => (
                <button key={a} type="button" onClick={() => setF({ ...f, acquisition: a })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${f.acquisition === a ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                  {ACQUISITION_LABEL[a]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={label} htmlFor="de-note">비고</label>
            <textarea id="de-note" className={input + ' h-20 resize-none'} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </div>
        </div>
        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};
